import React, { useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { IoIosWarning } from 'react-icons/io';
import { VscError } from 'react-icons/vsc';
import Palette from '../Palette';
import { useParams } from 'react-router-dom';
import MemberCerification from '../models/MemberCertification';
import moment from 'moment';

const sampleCertificates = [
  {
    id: 'CERT-2023-001',
    title: 'TSN Penerjemah (Indonesia-Prancis) Umum',
    issueDate: '15 Oktober 2023',
    expiryDate: '15 Oktober 2025',
    name: 'Indra Listyo',
    role: 'Ketua Umum HPI',
    date: '1 Juli 2025, 10:45:48',
    qrCode: 'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=CERT-2023-001',
    isInvalid: false,
    isExpired: false,
  },
];

function QRCertificatePage() {
  const { token } = useParams();
  const [certData, setCertData] = useState(null);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const isWarning = isInvalid || isExpired;

  const fetchCertificateDetail = async () => {
    try {
      setLoading(true);
      const response = await MemberCerification.getByToken(token);
      
      if (response.length === 0) {
        setIsInvalid(true);
      } else {
        const certificate = response[0];
        setCertData(certificate);
        
        if (moment() > moment(certificate.valid_until)) {
          setIsExpired(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch certificate:', error);
      setIsInvalid(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificateDetail();
  }, [token]);

  const getEmbedUrl = () => {
    if (!certData?.path) return '';
    return `https://docs.google.com/viewer?url=${encodeURIComponent(certData.path)}&embedded=true`;
  };

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row>
        <Col md={{ span: 10, offset: 1 }} xs={12}>
          {/* PDF Embed Section */}
          {!isWarning && certData?.path && (
            <div className="mb-4 shadow-sm" style={{ height: '80vh', borderRadius: '8px', overflow: 'hidden' }}>
              <iframe
                src={getEmbedUrl()}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="Certificate PDF Viewer"
                allowFullScreen
              />
            </div>
          )}

          {/* Invalid Certificate Display */}
          {isInvalid && (
            <div className="text-center p-4 rounded shadow-sm bg-white">
              <div className="text-danger mb-3">
                <div style={{
                  width: 120,
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                }}>
                  <VscError size={100} color="#ff0000" />
                </div>
              </div>
              <h5 className="fw-bold text-dark">Sertifikat Tidak Valid</h5>
              <p className="text-muted small mb-4">
                QR Code tidak cocok dengan sertifikat yang terdaftar resmi di sistem HPI
              </p>
            </div>
          )}

          {/* Expired Certificate Display */}
          {!isInvalid && isExpired && (
            <div className="text-center p-4 rounded shadow-sm bg-white">
              <div className="text-warning mb-3">
                <div style={{
                  width: 120,
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                }}>
                  <IoIosWarning size={100} color="#FFA500" />
                </div>
              </div>
              <h5 className="fw-bold text-dark">Sertifikat Kedaluwarsa</h5>
              <p className="text-muted small mb-4">
                Masa berlaku sertifikat berakhir{' '}
                <span className="fw-bold text-primary">
                  {moment(certData.valid_until).format('DD MMMM YYYY')}
                </span>
              </p>
              <Button
                variant="primary"
                style={{
                  backgroundColor: Palette.PRIMARY,
                  borderColor: Palette.PRIMARY,
                  borderRadius: '20px',
                  padding: '10px 30px',
                  fontWeight: 'bold',
                }}
                onClick={() => window.location.replace('/member/akun')}
              >
                Lihat Profil
              </Button>
            </div>
          )}

          {/* Certificate Metadata */}
          {!isWarning && certData && (
            <div className="bg-light rounded p-4 mb-4 shadow-sm">
              <Row className="mb-3">
                <Col xs={6} className="text-muted">
                  Nama Sertifikat
                </Col>
                <Col xs={6} className="fw-bold text-end" style={{ color: Palette.PRIMARY }}>
                  {certData.name}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={6} className="text-muted">
                  Nomor Sertifikat
                </Col>
                <Col xs={6} className="fw-bold text-end" style={{ color: Palette.PRIMARY }}>
                  {certData.certification_number}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={6} className="text-muted">
                  Tanggal Diterbitkan
                </Col>
                <Col xs={6} className="fw-bold text-end" style={{ color: Palette.PRIMARY }}>
                  {moment(certData.valid_from).format('DD MMMM YYYY')}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={6} className="text-muted">
                  Masa Berlaku
                </Col>
                <Col xs={6} className="fw-bold text-end" style={{ color: Palette.PRIMARY }}>
                  {moment(certData.valid_until).format('DD MMMM YYYY')}
                </Col>
              </Row>

              <hr />

              <p className="text-secondary mb-2">
                Sertifikat ini telah ditandatangani secara resmi oleh:
              </p>
              <Row className="align-items-center mb-2">
                <Col xs={12} md={8}>
                  <div className="fw-bold" style={{ color: Palette.PRIMARY }}>
                    Indra Listyo
                  </div>
                  <div className="text-muted">Ketua Umum HPI</div>
                </Col>
                <Col xs={12} md={4} className="text-md-end text-muted mt-2 mt-md-0">
                  {moment(certData.valid_from).format('DD MMMM YYYY, HH:mm:ss')}
                </Col>
              </Row>
              <Row className="align-items-center mb-2">
                <Col xs={12} md={8}>
                  <div className="fw-bold" style={{ color: Palette.PRIMARY }}>
                    Dr. Sugeng Hariyanto
                  </div>
                  <div className="text-muted">Atas Nama KKS</div>
                </Col>
                <Col xs={12} md={4} className="text-md-end text-muted mt-2 mt-md-0">
                  {moment(certData.valid_from).format('DD MMMM YYYY, HH:mm:ss')}
                </Col>
              </Row>

              <p className="text-start text-secondary mt-4">
                dan diakui oleh{' '}
                <span className="fw-bold" style={{ color: Palette.PRIMARY }}>
                  Himpunan Penerjemah Indonesia
                </span>
              </p>
            </div>
          )}

          {!isWarning && certData && (
            <div className="text-center">
              <Button
                variant="primary"
                style={{
                  backgroundColor: Palette.PRIMARY,
                  borderColor: Palette.PRIMARY,
                  borderRadius: '20px',
                  padding: '10px 30px',
                  fontWeight: 'bold',
                }}
                onClick={() => window.location.replace('/member/akun')}
              >
                Lihat Profil
              </Button>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default QRCertificatePage;